import React, { useState, useEffect } from "react";
import { ContactSection } from "./ContactSection";
import { UpdateContactModal } from "./UpdateContactModal";
import axios from "axios";
import { useAuthFetch } from "../../hooks/useAuthFetch";
import "./Contacts.css";

export function Contacts() {
  const { getAuthConfig, isReady } = useAuthFetch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [opportunityName, setOpportunityName] = useState('');
  const [contactSections, setContactSections] = useState({
    customerContacts: {
      title: "Nettles Project Team",
      contacts: [
        { title: "Operations Manager", contacts: [] },
        { title: "Project Manager", contacts: [] },
        { title: "Project Accountant", contacts: [] },
        { title: "Submittals", contacts: [] },
        { title: "Salesperson", contacts: [] },
        { title: "Superintendent", contacts: [] }
      ]
    },
    gcCustomer: {
      title: "GC/Customer",
      contacts: [
        { title: "Project Manager", contacts: [] },
        { title: "Asst. Project Manager", contacts: [] },
        { title: "Superintendent", contacts: [] },
        { title: "Project Engineer", contacts: [] },
        { title: "Billing Contact", contacts: [] }
      ]
    },
    Vendor: {
      title: "Vendors",
      contacts: [
        { 
          title: "Vendors",  // Add title for consistency
          contacts: [] 
        }
      ]
    },
    Design: {
      title: "Designer/Consultant",
      contacts: [
        { title: "Architect", contacts: [] },
        { title: "Engineer", contacts: [] },
        { title: "Inspector", contacts: [] },
        { title: "Consultant", contacts: [] }
      ]
    }
  });
  
  const [isArchivedModalOpen, setIsArchivedModalOpen] = useState(false);
  const [archivedContacts, setArchivedContacts] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archivedError, setArchivedError] = useState("");
  const [archivedSections, setArchivedSections] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);

  // Add function to fetch contacts
  const fetchContacts = async (opportunityUUID, triggerAutoPopulate = false) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/contacts/opportunity/${opportunityUUID}`,
        await getAuthConfig()
      );
      const transformedData = transformApiData(response.data);
      if (transformedData) {
        setContactSections(transformedData);
      }

      if (triggerAutoPopulate && transformedData) {
        const rolesToCheck = ['Operations Manager', 'Project Manager', 'Project Accountant', 'Submittals'];
        const nettlesTeamSections = transformedData?.customerContacts?.contacts || [];
        const hasEmptyRole = rolesToCheck.some(role => {
          const section = nettlesTeamSections.find(s => s.title === role);
          return !section || section.contacts.length === 0;
        });

        const projectData = localStorage.getItem('selectedProjectDetails');
        const parsed = projectData ? JSON.parse(projectData) : null;
        const branch = parsed?.company;
        const isAutoPopulateBranch = branch === 'Waller, TX' || branch === 'Kansas City, KS';

        let chatAlreadySynced = false;
        if (hasEmptyRole && isAutoPopulateBranch) {
          try {
            await axios.post(
              `${process.env.REACT_APP_BACKEND_BASEURL}/api/contacts/auto-populate`,
              { opportunityUUID, branch },
              await getAuthConfig()
            );
            chatAlreadySynced = true;
            const refreshResponse = await axios.get(
              `${process.env.REACT_APP_BACKEND_BASEURL}/api/contacts/opportunity/${opportunityUUID}`,
              await getAuthConfig()
            );
            const refreshedData = transformApiData(refreshResponse.data);
            if (refreshedData) {
              setContactSections(refreshedData);
            }
          } catch (autoPopError) {
            console.error('Auto-populate Nettles team failed:', autoPopError);
          }
        }

        if (!chatAlreadySynced) {
          try {
            await axios.post(
              `${process.env.REACT_APP_BACKEND_BASEURL}/api/contacts/sync-chat-members`,
              { opportunityUUID },
              await getAuthConfig()
            );
          } catch (syncError) {
            console.error('Sync Nettles team to Teams chat failed:', syncError);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchArchivedContacts = async (opportunityUUID) => {
    if (!isReady) return;
    setArchivedLoading(true);
    setArchivedError("");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/contacts/archived?Opportunity_UUID__c=${opportunityUUID}`,
        await getAuthConfig()
      );
      setArchivedContacts(response.data.data || []);
      setArchivedSections(transformArchivedData(response.data.data || []));
    } catch (error) {
      setArchivedError("Failed to fetch archived contacts");
    } finally {
      setArchivedLoading(false);
    }
  };

  useEffect(() => {
    // Get project details including opportunity name from localStorage
    const projectData = localStorage.getItem('selectedProjectDetails');
    if (projectData) {
      try {
        const parsed = JSON.parse(projectData);
        setOpportunityName(parsed.opportunityName);
        // Pass true so that missing Nettles team roles are auto-populated on first load
        fetchContacts(parsed.opportunityUUID, true);
      } catch (e) {
        console.error('Error parsing project data:', e);
      }
    }
  }, []);

  useEffect(() => {
    const handleOpenModal = (event) => {
      setSelectedSection(event.detail.sectionType);
      setSelectedContact(event.detail.contactData || null);
      // Store the contactType if provided
      if (event.detail.contactType) {
        localStorage.setItem('selectedContactType', event.detail.contactType);
      }
      setIsModalOpen(true);
    };

    window.addEventListener('openContactModal', handleOpenModal);
    return () => window.removeEventListener('openContactModal', handleOpenModal);
  }, []);

  // Add effect to listen for refresh events
  useEffect(() => {
    const handleRefresh = () => {
      // Get project details and refresh contacts
      const projectData = localStorage.getItem('selectedProjectDetails');
      if (projectData) {
        try {
          const parsed = JSON.parse(projectData);
          fetchContacts(parsed.opportunityUUID);
        } catch (e) {
          console.error('Error parsing project data:', e);
        }
      }
    };

    // Listen for the refresh event
    window.addEventListener('refreshContacts', handleRefresh);
    
    // Cleanup
    return () => window.removeEventListener('refreshContacts', handleRefresh);
  }, []);

  // Handle modal close and refresh
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedSection(null);
    setSelectedContact(null);
    localStorage.removeItem('selectedContactType');
    
    // Only refresh contacts data if modal was closed without successful update
    // (handleContactUpdated will have already updated the data for successful updates)
    // This prevents double-refresh issues
  };

  const handleContactUpdated = (updatedContacts) => {
    // Transform and set the updated contacts directly
    const transformedData = transformApiData({ data: updatedContacts });
    setContactSections(transformedData);
  };

  // Helper function to transform API data into our structure
  const transformApiData = (apiData) => {
    if (!apiData?.data) return;

    // First, group and filter contacts to keep only the latest entries
    // Use a more specific key that includes the contact's email or full name to allow multiple contacts of the same type
    const latestContacts = {};
    apiData.data.forEach(contact => {
      // Create a unique key per actual person, not just per contact type per account
      const uniqueIdentifier = contact.Email || `${contact.FirstName}-${contact.LastName}-${contact.AccountId}`;
      const key = `${contact.Opportunity_System_Wide_ID__c}-${contact.Group}-${contact.ContactType}-${uniqueIdentifier}`;
      if (!latestContacts[key] || latestContacts[key].id < contact.id) {
        latestContacts[key] = contact;
      }
    });

         // Initialize the structure with empty contacts arrays
     const sections = {
       customerContacts: {
         title: "Nettles Project Team",
         contacts: [
           { title: "Operations Manager", contacts: [] },
           { title: "Project Manager", contacts: [] },
           { title: "Project Accountant", contacts: [] },
           { title: "Submittals", contacts: [] },
           { title: "Salesperson", contacts: [] },
           { title: "Superintendent", contacts: [] }
         ]
       },
      gcCustomer: {
        title: "GC/Customer",
        contacts: [
          { title: "Project Manager", contacts: [] },
          { title: "Asst. Project Manager", contacts: [] },
          { title: "Superintendent", contacts: [] },
          { title: "Project Engineer", contacts: [] },
          { title: "Billing Contact", contacts: [] }
        ]
      },
      Vendor: {
        title: "Vendors",
        contacts: [
          { 
            title: "Vendors",  // Add title for consistency
            contacts: [] 
          }
        ]
      },
      Design: {
        title: "Designer/Consultant",
        contacts: [
          { title: "Architect", contacts: [] },
          { title: "Engineer", contacts: [] },
          { title: "Inspector", contacts: [] },
          { title: "Consultant", contacts: [] }
        ]
      }
    };

    // Debug logging to help track contact filtering
    console.log(`Total contacts from API: ${apiData.data.length}`);
    console.log(`Contacts after deduplication: ${Object.values(latestContacts).length}`);
    
    // Map filtered API data to our structure
    Object.values(latestContacts).forEach(contact => {
      const contactData = {
        id: contact.id,
        name: `${contact.FirstName} ${contact.LastName}`,
        title: contact.AccountName || '-',
        email: contact.Email || '-',
        phones: [
          contact.Phone || '',
          contact.MobilePhone || ''
        ],
        ...contact,
        hasChat: contact.Group === "Nettles Project Team"
      };

      // Map Group to our section keys
      const sectionMap = {
        "Nettles Project Team": "customerContacts",
        "GC/Customer": "gcCustomer",
        "Vendors": "Vendor",
        "Designer/Consultant": "Design"
      };

      const sectionKey = sectionMap[contact.Group];
      if (!sectionKey) return;

      if (contact.Group === "Vendors") {
        // Add all necessary contact information
        const vendorContact = {
          id: contact.id,
          name: `${contact.FirstName} ${contact.LastName}`,
          title: contact.AccountName || '-',
          email: contact.Email || '-',
          phones: [
            contact.Phone || '',
            contact.MobilePhone || ''
          ],
          department: contact.Department || '-',
          ...contact,
          hasChat: false
        };
        sections[sectionKey].contacts[0].contacts.push(vendorContact);
      } else {
        const section = sections[sectionKey].contacts.find(
          s => s.title === contact.ContactType
        );
        if (section) {
          section.contacts.push(contactData);
          console.log(`Added contact ${contact.FirstName} ${contact.LastName} to ${contact.Group} - ${contact.ContactType}`);
        } else {
          console.log(`No section found for contact type: ${contact.ContactType} in group: ${contact.Group}`);
        }
      }
    });

    return sections;
  };

  // Helper function to transform archived API data into our structure
  const transformArchivedData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return;

    // Initialize the structure with empty contacts arrays
    const sections = {
      customerContacts: {
        title: "Nettles Project Team",
        contacts: [
          { title: "Operations Manager", contacts: [] },
          { title: "Project Manager", contacts: [] },
          { title: "Project Accountant", contacts: [] },
          { title: "Submittals", contacts: [] },
          { title: "Salesperson", contacts: [] },
          { title: "Superintendent", contacts: [] }
        ]
      },
      gcCustomer: {
        title: "GC/Customer",
        contacts: [
          { title: "Project Manager", contacts: [] },
          { title: "Asst. Project Manager", contacts: [] },
          { title: "Superintendent", contacts: [] },
          { title: "Project Engineer", contacts: [] },
          { title: "Billing Contact", contacts: [] }
        ]
      },
      Vendor: {
        title: "Vendors",
        contacts: [
          { 
            title: "Vendors",
            contacts: [] 
          }
        ]
      },
      Design: {
        title: "Designer/Consultant",
        contacts: [
          { title: "Architect", contacts: [] },
          { title: "Engineer", contacts: [] },
          { title: "Inspector", contacts: [] },
          { title: "Consultant", contacts: [] }
        ]
      }
    };

    // Map all contacts to our structure
    apiData.forEach(contact => {
      const contactData = {
        id: contact.id,
        name: `${contact.FirstName} ${contact.LastName}`,
        title: contact.AccountName || '-',
        email: contact.Email || '-',
        phones: [
          contact.Phone || '',
          contact.MobilePhone || ''
        ],
        ...contact,
        hasChat: contact.Group === "Nettles Project Team"
      };

      // Map Group to our section keys
      const sectionMap = {
        "Nettles Project Team": "customerContacts",
        "GC/Customer": "gcCustomer",
        "Vendors": "Vendor",
        "Designer/Consultant": "Design"
      };

      const sectionKey = sectionMap[contact.Group];
      if (!sectionKey) return;

      if (contact.Group === "Vendors") {
        sections[sectionKey].contacts[0].contacts.push(contactData);
      } else {
        const section = sections[sectionKey].contacts.find(
          s => s.title === contact.ContactType
        );
        if (section) {
          section.contacts.push(contactData);
        }
      }
    });

    return sections;
  };

  return (
    <div className="dashboard-contacts flex flex-col px-8 max-md:px-4 pt-4 max-md:pt-3 pb-4 max-md:pb-3 mx-auto w-full rounded-xl bg-stone-50 max-md:max-w-full overflow-hidden">
      <div className="dashboard-contacts-header flex justify-between items-center max-md:flex-wrap max-md:gap-2">
        <div className="dashboard-contacts-title text-xl max-md:text-lg font-semibold text-zinc-800">
          Contacts
        </div>
        <button
          className="dashboard-contacts-archived text-red-600 text-sm hover:underline ml-4"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          onClick={() => {
            const projectData = localStorage.getItem('selectedProjectDetails');
            if (projectData) {
              try {
                const parsed = JSON.parse(projectData);
                fetchArchivedContacts(parsed.opportunityUUID);
                setIsArchivedModalOpen(true);
              } catch (e) {
                setArchivedError('Could not get opportunity UUID');
                setIsArchivedModalOpen(true);
              }
            } else {
              setArchivedError('No project selected');
              setIsArchivedModalOpen(true);
            }
          }}
        >
          Archived
        </button>
      </div>
      <div className="dashboard-contacts-list mt-4">
        {Object.entries(contactSections).map(([key, section]) => (
          <ContactSection
            key={key}
            title={section.title}
            sections={section.contacts}
            sectionType={key}
            onRefresh={() => {
              const projectData = localStorage.getItem('selectedProjectDetails');
              if (projectData) {
                try {
                  const parsed = JSON.parse(projectData);
                  fetchContacts(parsed.opportunityUUID);
                } catch (e) {
                  console.error('Error parsing project data:', e);
                }
              }
            }}
          />
        ))}
      </div>

      {isModalOpen && (
        <UpdateContactModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          sectionType={selectedSection}
          contact={selectedContact}
          onContactUpdated={handleContactUpdated}
        />
      )}

      {/* Archived Contacts Modal */}
      {isArchivedModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[96rem] max-w-[90vw] max-h-[90vh] relative">
            <div className="flex justify-between items-center px-5 py-4 border-b">
              <div className="text-xl font-semibold">Archived Contacts</div>
              <button
                onClick={() => setIsArchivedModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="px-5 py-4 overflow-y-auto max-h-[calc(90vh-80px)] [&::-webkit-scrollbar]:w-2 
              [&::-webkit-scrollbar-track]:bg-gray-100 
              [&::-webkit-scrollbar-thumb]:bg-gray-300
              [&::-webkit-scrollbar-thumb]:rounded-full 
              [&::-webkit-scrollbar-track]:rounded-full">
              {archivedLoading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                </div>
              ) : archivedError ? (
                <div className="text-center text-red-500">{archivedError}</div>
              ) : archivedSections ? (
                <>
                  {Object.entries(archivedSections).map(([key, section]) => (
                    <div key={key}>
                      <ContactSection
                        title={section.title}
                        sections={section.contacts}
                        sectionType={key}
                        hideUpdate={true}
                      />
                    </div>
                  ))}
                </>
              ) : (
                <div className="text-center text-gray-500">No archived contacts found.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
